import React, { useState, useRef } from 'react';
import { FaTrash, FaCompress, FaImages, FaUpload, FaEye, FaExpand } from 'react-icons/fa';

const ImageManagement = ({ 
  currentAvatar = null,
  currentSelfies = [],
  onImageUpload,
  onImageDelete,
  maxAvatarSize = 1, // MB
  maxSelfieSize = 5, // MB
  maxSelfieCount = 10,
  totalSelfieSize = 0,
  maxTotalSelfieSize = 20 // MB
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [compressionSuggestions, setCompressionSuggestions] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);

  // Calculate available space for selfies
  const availableSpace = maxTotalSelfieSize - (totalSelfieSize / (1024 * 1024));
  const remainingSelfies = maxSelfieCount - currentSelfies.length;

  // Image compression utility
  const compressImage = (file, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max width/height of 1024px)
        const maxDimension = 1024;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          resolve(compressedFile);
        }, file.type, quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Validate and process files
  const validateAndProcessFiles = async (files, type = 'selfie') => {
    const maxSize = type === 'avatar' ? maxAvatarSize : maxSelfieSize;
    const maxSizeBytes = maxSize * 1024 * 1024;
    const validFiles = [];
    const suggestions = [];

    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        continue;
      }

      let processedFile = file;
      let needsCompression = false;

      // Check if compression is needed
      if (file.size > maxSizeBytes) {
        needsCompression = true;
        suggestions.push({
          fileName: file.name,
          originalSize: file.size,
          maxSize: maxSizeBytes,
          type: 'size'
        });

        // Auto-compress if over limit
        try {
          processedFile = await compressImage(file, 0.7);
          if (processedFile.size > maxSizeBytes) {
            processedFile = await compressImage(file, 0.5);
          }
        } catch (error) {
          console.error('Compression failed:', error);
        }
      }

      // Check dimensions and suggest compression for large images
      const img = new Image();
      img.onload = () => {
        if (img.width > 1024 || img.height > 1024) {
          suggestions.push({
            fileName: file.name,
            originalSize: file.size,
            dimensions: `${img.width}x${img.height}`,
            type: 'dimensions'
          });
        }
      };
      img.src = URL.createObjectURL(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = {
          file: processedFile,
          base64: e.target.result.split(',')[1],
          name: processedFile.name,
          size: processedFile.size,
          type: processedFile.type,
          preview: e.target.result,
          originalSize: file.size,
          compressed: needsCompression
        };
        validFiles.push(imageData);
      };
      reader.readAsDataURL(processedFile);
    }

    setCompressionSuggestions(suggestions);
    return validFiles;
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handleFiles = async (files) => {
    const processedFiles = await validateAndProcessFiles(files);
    setSelectedImages(prev => [...prev, ...processedFiles]);
    setPreviewImages(prev => [...prev, ...processedFiles]);
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (selectedImages.length === 0) {
      alert('Please select images first');
      return;
    }

    for (const imageData of selectedImages) {
      await onImageUpload(imageData, 'selfie');
    }

    setSelectedImages([]);
    setPreviewImages([]);
    setShowBulkUpload(false);
  };

  // Remove image from selection
  const removeFromSelection = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  // Image preview modal
  const openPreview = (image) => {
    setPreviewImage(image);
    setShowPreview(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaImages className="text-blue-400" />
            Image Management
          </h3>
          <p className="text-sm text-gray-400">
            Manage avatar and selfie images for your character
          </p>
        </div>
        <button
          onClick={() => setShowBulkUpload(!showBulkUpload)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <FaUpload />
          Bulk Upload
        </button>
      </div>

      {/* Storage Info */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-300">Selfie Storage</span>
          <span className="text-sm text-gray-400">
            {formatFileSize(totalSelfieSize)} / {maxTotalSelfieSize}MB
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${
              (totalSelfieSize / (1024 * 1024)) > maxTotalSelfieSize * 0.9 
                ? 'bg-red-500' 
                : (totalSelfieSize / (1024 * 1024)) > maxTotalSelfieSize * 0.7 
                  ? 'bg-yellow-500' 
                  : 'bg-green-500'
            }`}
            style={{ width: `${((totalSelfieSize / (1024 * 1024)) / maxTotalSelfieSize) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{currentSelfies.length} / {maxSelfieCount} images</span>
          <span>{availableSpace.toFixed(1)}MB available</span>
        </div>
      </div>

      {/* Compression Suggestions */}
      {compressionSuggestions.length > 0 && (
        <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FaCompress className="text-yellow-400" />
            <h4 className="font-semibold text-yellow-300">Compression Suggestions</h4>
          </div>
          <ul className="text-sm text-yellow-200 space-y-1">
            {compressionSuggestions.map((suggestion, index) => (
              <li key={index}>
                <strong>{suggestion.fileName}:</strong> {
                  suggestion.type === 'size' 
                    ? `File size ${formatFileSize(suggestion.originalSize)} exceeds ${formatFileSize(suggestion.maxSize)} limit`
                    : `Large dimensions ${suggestion.dimensions} - consider resizing`
                }
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Current Avatar */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="font-semibold text-white mb-3">Current Avatar</h4>
        <div className="flex items-center gap-4">
          {currentAvatar ? (
            <div className="relative">
              <img
                src={currentAvatar}
                alt="Current avatar"
                className="w-20 h-20 rounded-full object-cover border border-gray-600"
              />
              <button
                onClick={() => openPreview({ preview: currentAvatar, name: 'Current Avatar' })}
                className="absolute -top-2 -right-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
              >
                <FaEye />
              </button>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
              <FaImages className="text-gray-500" />
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-gray-300">
              {currentAvatar ? 'Avatar uploaded' : 'No avatar set'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  const processedFiles = await validateAndProcessFiles(files, 'avatar');
                  if (processedFiles.length > 0) {
                    await onImageUpload(processedFiles[0], 'avatar');
                  }
                }
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm mt-2"
            >
              {currentAvatar ? 'Change Avatar' : 'Upload Avatar'}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Upload Interface */}
      {showBulkUpload && (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
          <h4 className="font-semibold text-white mb-3">Bulk Upload Selfies</h4>
          
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
              dragActive 
                ? 'border-blue-400 bg-blue-50 bg-opacity-10' 
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => bulkInputRef.current?.click()}
          >
            <input
              ref={bulkInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                await handleFiles(files);
              }}
            />
            <FaUpload className="mx-auto text-4xl text-gray-400 mb-2" />
            <p className="text-white mb-1">Drop images here or click to select</p>
            <p className="text-sm text-gray-400">
              You can upload up to {remainingSelfies} more selfies ({availableSpace.toFixed(1)}MB available)
            </p>
          </div>

          {/* Preview selected images */}
          {previewImages.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-white mb-2">Selected Images ({previewImages.length})</h5>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {previewImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-full h-24 object-cover rounded border border-gray-600"
                    />
                    <button
                      onClick={() => removeFromSelection(index)}
                      className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                    >
                      <FaTrash />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 rounded-b">
                      {formatFileSize(image.size)}
                      {image.compressed && <span className="text-green-400 ml-1">✓</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleBulkUpload}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                  <FaUpload />
                  Upload All ({previewImages.length})
                </button>
                <button
                  onClick={() => {
                    setSelectedImages([]);
                    setPreviewImages([]);
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Current Selfies */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="font-semibold text-white mb-3">Current Selfies ({currentSelfies.length})</h4>
        {currentSelfies.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {currentSelfies.map((selfie, index) => (
              <div key={index} className="relative group">
                <img
                  src={selfie.url || selfie}
                  alt={`Selfie ${index + 1}`}
                  className="w-full h-24 object-cover rounded border border-gray-600"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => openPreview({ preview: selfie.url || selfie, name: `Selfie ${index + 1}` })}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center"
                  >
                    <FaExpand />
                  </button>
                  <button
                    onClick={() => onImageDelete(selfie, 'selfie', index)}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <FaImages className="mx-auto text-4xl mb-2" />
            <p>No selfies uploaded yet</p>
            <p className="text-sm">Use bulk upload to add multiple selfies at once</p>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {showPreview && previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setShowPreview(false)}>
          <div className="max-w-4xl max-h-4xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img
                src={previewImage.preview}
                alt={previewImage.name}
                className="max-w-full max-h-full object-contain rounded"
              />
              <button
                onClick={() => setShowPreview(false)}
                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-3 py-1 rounded">
                {previewImage.name}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManagement;